import { createContext, useContext, useEffect, useRef, useState, ReactNode } from "react";
import type { RealtimeChannel } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "./use-auth";

type PresenceStatus = "online" | "offline" | "busy";

interface PresenceEntry {
  status: PresenceStatus;
  lastSeen: string;
  hiddenFrom?: string[];
}

interface PresenceState {
  [userId: string]: PresenceEntry;
}

const STORAGE_KEY = "taskratel:appear-online";
const HIDDEN_KEY = "taskratel:hidden-from-peers";
const LASTSEEN_KEY = "taskratel:last-seen-map";
const HEARTBEAT_MS = 20000;

function readPref(): boolean {
  if (typeof window === "undefined") return true;
  return window.localStorage.getItem(STORAGE_KEY) !== "false";
}

function readJson<T>(key: string, fallback: T): T {
  if (typeof window === "undefined") return fallback;
  try {
    const raw = window.localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : fallback;
  } catch {
    return fallback;
  }
}

function writeJson(key: string, value: unknown) {
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* ignore */
  }
}

export function formatLastSeen(iso?: string | null): string {
  if (!iso) return "Last seen a while ago";
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "Last seen a while ago";
  const mins = Math.max(0, Math.round((Date.now() - then) / 60000));
  if (mins < 1) return "Last seen just now";
  if (mins < 60) return `Last seen ${mins}m ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `Last seen ${hours}h ago`;
  const days = Math.round(hours / 24);
  if (days < 7) return `Last seen ${days}d ago`;
  return `Last seen ${new Date(iso).toLocaleDateString()}`;
}

interface PresenceContextType {
  presenceState: PresenceState;
  myStatus: PresenceStatus;
  setMyStatus: (status: PresenceStatus) => void;
  appearOnline: boolean;
  setAppearOnline: (value: boolean) => void;
  getProviderStatus: (providerId: string) => { status: PresenceStatus; lastSeen: string } | null;
  isHiddenFrom: (peerId: string) => boolean;
  setHiddenFrom: (peerId: string, hidden: boolean) => void;
}

const PresenceContext = createContext<PresenceContextType>({
  presenceState: {},
  myStatus: "offline",
  setMyStatus: () => {},
  appearOnline: true,
  setAppearOnline: () => {},
  getProviderStatus: () => null,
  isHiddenFrom: () => false,
  setHiddenFrom: () => {},
});

export function PresenceProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const [presenceState, setPresenceState] = useState<PresenceState>({});
  const [appearOnline, setAppearOnlineState] = useState<boolean>(readPref);
  const [manualStatus, setManualStatus] = useState<PresenceStatus>("online");
  const [hiddenPeers, setHiddenPeers] = useState<Record<string, boolean>>(() =>
    readJson<Record<string, boolean>>(HIDDEN_KEY, {})
  );
  const [lastSeenMap, setLastSeenMap] = useState<Record<string, string>>(() =>
    readJson<Record<string, string>>(LASTSEEN_KEY, {})
  );
  const channelRef = useRef<RealtimeChannel | null>(null);

  const myStatus: PresenceStatus = !user || !appearOnline ? "offline" : manualStatus;
  const hiddenList = Object.entries(hiddenPeers)
    .filter(([, v]) => v)
    .map(([k]) => k);
  const hiddenKey = hiddenList.slice().sort().join(",");

  const rememberLastSeen = (entries: PresenceState) => {
    setLastSeenMap((prev) => {
      const next = { ...prev };
      let changed = false;
      for (const [id, e] of Object.entries(entries)) {
        if (e.lastSeen && next[id] !== e.lastSeen) {
          next[id] = e.lastSeen;
          changed = true;
        }
      }
      if (changed) writeJson(LASTSEEN_KEY, next);
      return changed ? next : prev;
    });
  };

  useEffect(() => {
    if (!user) {
      channelRef.current = null;
      return;
    }

    const channel = supabase.channel("presence:providers", {
      config: { presence: { key: user.id } },
    });
    channelRef.current = channel;

    const applySync = () => {
      const state = channel.presenceState();
      const next: PresenceState = {};
      for (const [userId, data] of Object.entries(state)) {
        const entries = data as unknown as PresenceEntry[];
        if (entries.length > 0) {
          next[userId] = {
            status: entries[0].status || "online",
            lastSeen: entries[0].lastSeen || new Date().toISOString(),
            hiddenFrom: entries[0].hiddenFrom || [],
          };
        }
      }
      rememberLastSeen(next);
      setPresenceState(next);
    };

    channel
      .on("presence", { event: "sync" }, applySync)
      .on("presence", { event: "join" }, applySync)
      .on("presence", { event: "leave" }, ({ key }) => {
        const seen = new Date().toISOString();
        setLastSeenMap((prev) => {
          const next = { ...prev, [key]: seen };
          writeJson(LASTSEEN_KEY, next);
          return next;
        });
        setPresenceState((prev) => ({
          ...prev,
          [key]: { status: "offline", lastSeen: seen, hiddenFrom: [] },
        }));
      })
      .subscribe(async (status) => {
        if (status === "SUBSCRIBED") {
          await channel.track({
            status: myStatus,
            lastSeen: new Date().toISOString(),
            hiddenFrom: hiddenList,
          });
        }
      });

    return () => {
      channelRef.current = null;
      supabase.removeChannel(channel);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  // Re-broadcast whenever the effective status or per-chat visibility changes
  useEffect(() => {
    const channel = channelRef.current;
    if (!channel || !user) return;
    channel.track({ status: myStatus, lastSeen: new Date().toISOString(), hiddenFrom: hiddenList });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [myStatus, user, hiddenKey]);

  // Heartbeat keeps the badge accurate even on long idle sessions
  useEffect(() => {
    if (!user) return;
    const id = window.setInterval(() => {
      const channel = channelRef.current;
      if (!channel) return;
      channel.track({
        status: myStatus,
        lastSeen: new Date().toISOString(),
        hiddenFrom: hiddenList,
      });
    }, HEARTBEAT_MS);
    return () => window.clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, myStatus, hiddenKey]);

  const setAppearOnline = (value: boolean) => {
    setAppearOnlineState(value);
    try {
      window.localStorage.setItem(STORAGE_KEY, String(value));
    } catch {
      /* ignore */
    }
  };

  const setHiddenFrom = (peerId: string, hidden: boolean) => {
    setHiddenPeers((prev) => {
      const next = { ...prev, [peerId]: hidden };
      writeJson(HIDDEN_KEY, next);
      return next;
    });
  };

  const isHiddenFrom = (peerId: string) => !!hiddenPeers[peerId];

  const getProviderStatus = (providerId: string) => {
    const entry = presenceState[providerId];
    const lastSeen = entry?.lastSeen || lastSeenMap[providerId] || "";
    if (!entry) return lastSeen ? { status: "offline" as PresenceStatus, lastSeen } : null;
    // Respect the provider's per-chat visibility choice
    if (user && entry.hiddenFrom?.includes(user.id)) {
      return { status: "offline" as PresenceStatus, lastSeen };
    }
    return { status: entry.status, lastSeen };
  };

  return (
    <PresenceContext.Provider
      value={{
        presenceState,
        myStatus,
        setMyStatus: setManualStatus,
        appearOnline,
        setAppearOnline,
        getProviderStatus,
        isHiddenFrom,
        setHiddenFrom,
      }}
    >
      {children}
    </PresenceContext.Provider>
  );
}

export const usePresence = () => useContext(PresenceContext);

export function PresenceBadge({
  providerId,
  className = "",
  dotOnly = false,
  showLastSeen = false,
}: {
  providerId: string;
  className?: string;
  dotOnly?: boolean;
  showLastSeen?: boolean;
}) {
  const { getProviderStatus } = usePresence();
  const info = getProviderStatus(providerId);
  const status: PresenceStatus = info?.status || "offline";

  const statusColors: Record<PresenceStatus, string> = {
    online: "bg-green-500",
    offline: "bg-muted-foreground/50",
    busy: "bg-yellow-500",
  };
  const statusLabels: Record<PresenceStatus, string> = {
    online: "Online",
    offline: "Offline",
    busy: "Busy",
  };

  const lastSeenText = status === "offline" && info?.lastSeen ? formatLastSeen(info.lastSeen) : "";

  if (dotOnly) {
    return (
      <span
        title={lastSeenText || statusLabels[status]}
        aria-label={lastSeenText || statusLabels[status]}
        className={`inline-block h-2.5 w-2.5 rounded-full ring-2 ring-background ${statusColors[status]} ${className}`}
      />
    );
  }

  return (
    <span className={`inline-flex items-center gap-1.5 ${className}`}>
      <span className={`h-2 w-2 rounded-full ${statusColors[status]}`} />
      <span className="text-xs text-muted-foreground">
        {showLastSeen && lastSeenText ? lastSeenText : statusLabels[status]}
      </span>
    </span>
  );
}
